import { ProductDuration } from "../../Models/OrderBookEntry";
import { useMessages } from "../../Services/Nats";
import { usePrefillContext } from "../../Services/PrefillContextProvider";
import { ProductType } from "../../Services/nats/contracts";
import { Exchange } from "../../Services/ExchangeSelectorContextProvider";
import { AREA_CLUSTERS, clusterOfArea } from "./areaClusters";
import { kilowattsToMegawatts } from "../../megawatts";
import { useMemo } from "react";
import { spotKey, useSpotPriceLookup } from "../spotPrices/useSpotPriceLookup";
import { Price } from "../../price";

type AreaQuote = {
  areaCode: string;
  contractId: number;
  contractName: string;
  productDuration: ProductDuration;
  /**
   * False when the venue is not accepting orders on this contract even though
   * delivery is still ahead — a suspension, or a slot that has already gate
   * closed. Such a member carries no bid or ask at all (see
   * {@link useAllGridsOrderbook}), so it reads as dead rather than as a quote
   * nobody has hit.
   */
  openForTrading: boolean;
  bestBid: Price | undefined;
  bestAsk: Price | undefined;
  bidMw: number | undefined;
  askMw: number | undefined;
  topBidIsOwn: boolean;
  topAskIsOwn: boolean;
  /** Day-ahead spot price for this area and delivery window, if it has cleared. */
  spot: Price | undefined;
  product: string;
  productType: ProductType;
  exchange?: Exchange;
};

const toMw = kilowattsToMegawatts;

export type ClusterQuote = {
  clusterId: string;
  bidArea: string | undefined;
  askArea: string | undefined;
  areaCodes: string[];
  contractName: string;
  productDuration: ProductDuration;
  bestBid: Price | undefined;
  bestAsk: Price | undefined;
  bidMw: number | undefined;
  askMw: number | undefined;
  topBidIsOwn: boolean;
  topAskIsOwn: boolean;
  /**
   * The member area our own top bid / ask rests in, when the cell is ours.
   * Usually not {@link bidArea} / {@link askArea}: those are the first member
   * quoting the price, and in a coupled cluster that is Tohoku even when our
   * order is in Tokyo.
   */
  bidOwnArea: string | undefined;
  askOwnArea: string | undefined;
  product: string;
  productType: ProductType;
  exchange?: Exchange;
  bidDecoupled: boolean;
  askDecoupled: boolean;
  /**
   * The first member area that cleared. The cell renders every member from
   * {@link members} rather than this, so it serves two narrower purposes: the
   * sort/filter projection (see {@link WideOrderBookRow}) and the "has anything
   * cleared in this cluster at all" test.
   */
  spot: Price | undefined;
  /** Which member area {@link spot} came from. */
  spotArea: string | undefined;
  /**
   * True when member areas cleared at different spot prices — which is exactly
   * what decoupling means, so it is expected rather than exceptional.
   */
  spotDecoupled: boolean;
  /** How many of {@link members} the venue is currently accepting orders on. */
  openMemberCount: number;
  /**
   * No member of this cluster is open for trading. The cell renders dimmed and
   * inert instead of as an empty book — an empty book means nobody has quoted,
   * which is a very different thing to act on.
   */
  suspended: boolean;
  members: AreaQuote[];
};

/**
 * A delivery window, with one {@link ClusterQuote} per cluster.
 *
 * The flat `c<id>_bid` / `_ask` / `_spot` fields are the grid's sort and filter
 * projection of {@link cells}, in yen/kWh — DataGrid cannot order or filter a
 * `Price` object, and the renderers read `cells` instead. They are duplicated on
 * purpose; removing them silently disables sorting on every price column.
 */
export type WideOrderBookRow = {
  id: string;
  contractDeliveryStartUtc: string;
  contractDeliveryEndUtc: string;
  /** Not one contract in any area of this delivery window is open for trading. */
  suspended: boolean;
  cells: Record<string, ClusterQuote>;
  [field: `c${string}_bid`]: number | undefined;
  [field: `c${string}_ask`]: number | undefined;
  [field: `c${string}_spot`]: number | undefined;
};

export const rowId = (deliveryStart: string, deliveryEnd: string) =>
  `${deliveryStart}|${deliveryEnd}`;

export const bidField = (clusterId: string) => `c${clusterId}_bid` as const;
export const askField = (clusterId: string) => `c${clusterId}_ask` as const;
export const spotField = (clusterId: string) => `c${clusterId}_spot` as const;

// Feeds both the per-cluster hide button (columnVisibilityModel) and the column
// group's children. A cluster field missing from here renders outside its group
// header and does not hide with the cluster.
export const clusterFields = (clusterId: string) => [
  bidField(clusterId),
  askField(clusterId),
  spotField(clusterId),
];

export const clusterIdOfField = (field: string): string | undefined =>
  /^c(.+)_(?:bid|ask|spot)$/.exec(field)?.[1];

const firstDefinedIndex = (values: (Price | undefined)[]): number => {
  for (let i = 0; i < values.length; i++) {
    const value = values[i];
    if (value !== undefined && value !== null) {
      return i;
    }
  }
  return -1;
};

/**
 * Keyed on the numeric value, not the `Price` itself: a `Set` of objects dedups
 * on identity, so every cluster would look decoupled.
 */
const hasDiverged = (values: (Price | undefined)[]): boolean => {
  const defined = new Set<number>();
  for (const value of values) {
    if (value !== undefined && value !== null) {
      defined.add(value.yenPerMwh);
    }
  }
  return defined.size > 1;
};

/**
 * The member area holding our own order on top at the price the cluster cell
 * shows, or `undefined` when none does. Its presence is what colours the cell;
 * its value is where a click on it should open the depth ladder.
 *
 * Not just the flag of the area the price was taken from: coupled areas quote
 * the same price, the cell takes it from the *first* member with one (Tohoku
 * before Tokyo), and a quote's `is_own` is per area. So our ask resting in
 * Tokyo sat behind Tohoku's identical, not-own ask and never turned the cell
 * blue. Matched on the price, so a decoupled member whose own top is not the
 * one on screen does not colour it.
 */
const ownAreaAtPrice = (
  members: AreaQuote[],
  shown: Price | undefined,
  side: (m: AreaQuote) => [Price | undefined, boolean]
): string | undefined => {
  if (shown === undefined) {
    return undefined;
  }
  return members.find((m) => {
    const [price, own] = side(m);
    return own && shown.equals(price);
  })?.areaCode;
};

export const useAllGridsOrderbook = (): WideOrderBookRow[] => {
  const { contracts, topQuotes } = useMessages();
  // Fetched once per trading day and referentially stable between fetches, so
  // this adds no churn to the 500ms contracts/quotes tick.
  const spotPrices = useSpotPriceLookup();

  return useMemo(() => {
    const byWindow = new Map<string, Map<string, AreaQuote[]>>();
    const windowBounds = new Map<string, { start: string; end: string }>();
    // The span the venue is actually trading, from open contracts only. Closed
    // contracts outside it are the next session's slots, which the venue has
    // simply not opened yet — roughly fifty dead delivery windows on JEPX, and
    // not what "suspended" means. Inside it, a closed contract is a hole a
    // trader needs to see. ISO strings are all Z-suffixed, so they compare
    // lexicographically.
    let openSpanStart: string | undefined;
    let openSpanEnd: string | undefined;

    for (const areaId in contracts) {
      const areaContracts = contracts[areaId];
      const areaQuotes = topQuotes[areaId];

      for (const contractId in areaContracts) {
        const contract = areaContracts[contractId];

        if (contract === undefined) {
          continue;
        }

        const openForTrading = contract.open_for_trading;

        if (openForTrading) {
          if (
            openSpanStart === undefined ||
            contract.delivery_start < openSpanStart
          ) {
            openSpanStart = contract.delivery_start;
          }
          if (
            openSpanEnd === undefined ||
            contract.delivery_end > openSpanEnd
          ) {
            openSpanEnd = contract.delivery_end;
          }
        }

        const start = contract.delivery_start;
        const end = contract.delivery_end;
        const id = rowId(start, end);
        const areaCode = contract.contract_identifier.delivery_area_id;
        const clusterId = clusterOfArea(areaCode).id;

        let clusters = byWindow.get(id);
        if (clusters === undefined) {
          clusters = new Map<string, AreaQuote[]>();
          byWindow.set(id, clusters);
          windowBounds.set(id, { start, end });
        }

        let members = clusters.get(clusterId);
        if (members === undefined) {
          members = [];
          clusters.set(clusterId, members);
        }

        // Deliberately not read for a closed contract. PRICES is
        // `Maximum Per Subject: 1`, so the last quote a suspended contract ever
        // had sits there indefinitely — rendering it would put a green bid on a
        // row nobody can trade. Spot below is fine: it is a cleared day-ahead
        // reference, not a live side.
        const quote = openForTrading ? areaQuotes?.[contractId] : undefined;

        members.push({
          areaCode,
          contractId: contract.contract_identifier.contract_id,
          contractName: contract.name,
          productDuration: contract.product_duration,
          openForTrading,
          bestBid: quote?.top_bid?.price,
          bestAsk: quote?.top_ask?.price,
          bidMw: toMw(quote?.top_bid?.kilo_watts),
          askMw: toMw(quote?.top_ask?.kilo_watts),
          topBidIsOwn: quote?.top_bid?.is_own ?? false,
          topAskIsOwn: quote?.top_ask?.is_own ?? false,
          // Joined per area, not per cluster: coupled areas can and do clear at
          // different prices, and collapsing before that is known would hide it.
          spot: spotPrices.get(spotKey(areaCode, start)),
          product: contract.product_name,
          productType: contract.product_type,
          exchange: contract.exchange,
        });
      }
    }

    const rows: WideOrderBookRow[] = [];

    for (const [id, clusters] of byWindow) {
      const bounds = windowBounds.get(id);
      if (bounds === undefined) {
        continue;
      }

      let rowOpenMembers = 0;
      for (const members of clusters.values()) {
        for (const member of members) {
          if (member.openForTrading) {
            rowOpenMembers++;
          }
        }
      }

      const row: WideOrderBookRow = {
        id,
        contractDeliveryStartUtc: bounds.start,
        contractDeliveryEndUtc: bounds.end,
        suspended: rowOpenMembers === 0,
        cells: {},
      };

      for (const [clusterId, members] of clusters) {
        members.sort(
          (a, b) =>
            memberIndex(clusterId, a.areaCode) -
            memberIndex(clusterId, b.areaCode)
        );

        const bids = members.map((m) => m.bestBid);
        const asks = members.map((m) => m.bestAsk);
        const spots = members.map((m) => m.spot);

        // By index rather than by matching the value back against the members:
        // `Price` is a class, so `m.bestBid === bestBid` would be identity and
        // would happen to work only because it is the very same instance. Index
        // says what was actually meant — the first member that had one.
        const bidIndex = firstDefinedIndex(bids);
        const askIndex = firstDefinedIndex(asks);
        const spotIndex = firstDefinedIndex(spots);

        const bidSource = bidIndex === -1 ? undefined : members[bidIndex];
        const askSource = askIndex === -1 ? undefined : members[askIndex];
        const spotSource = spotIndex === -1 ? undefined : members[spotIndex];

        const bestBid = bidSource?.bestBid;
        const bestAsk = askSource?.bestAsk;
        const spot = spotSource?.spot;

        const bidOwnArea = ownAreaAtPrice(members, bestBid, (m) => [
          m.bestBid,
          m.topBidIsOwn,
        ]);
        const askOwnArea = ownAreaAtPrice(members, bestAsk, (m) => [
          m.bestAsk,
          m.topAskIsOwn,
        ]);

        const source = bidSource ?? askSource ?? members[0];

        let openMemberCount = 0;
        for (const member of members) {
          if (member.openForTrading) {
            openMemberCount++;
          }
        }

        row.cells[clusterId] = {
          clusterId,
          bidArea: bidSource?.areaCode,
          askArea: askSource?.areaCode,
          areaCodes: members.map((m) => m.areaCode),
          contractName: source.contractName,
          productDuration: source.productDuration,
          bestBid,
          bestAsk,
          bidMw: bidSource?.bidMw,
          askMw: askSource?.askMw,
          topBidIsOwn: bidOwnArea !== undefined,
          topAskIsOwn: askOwnArea !== undefined,
          bidOwnArea,
          askOwnArea,
          product: source.product,
          productType: source.productType,
          exchange: source.exchange,
          bidDecoupled: hasDiverged(bids),
          askDecoupled: hasDiverged(asks),
          spot,
          spotArea: spotSource?.areaCode,
          spotDecoupled: hasDiverged(spots),
          openMemberCount,
          suspended: openMemberCount === 0,
          members,
        };

        // The grid's sort/filter projection — see WideOrderBookRow.
        row[bidField(clusterId)] = bestBid?.yenPerKwh;
        row[askField(clusterId)] = bestAsk?.yenPerKwh;
        row[spotField(clusterId)] = spot?.yenPerKwh;
      }

      rows.push(row);
    }

    return rows;
  }, [contracts, topQuotes, spotPrices]);
};

const memberIndex = (clusterId: string, areaCode: string): number => {
  const cluster = AREA_CLUSTERS.find((c) => c.id === clusterId);
  const index = cluster?.areaCodes.indexOf(areaCode) ?? -1;
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

export type SelectedProducts = {
  rowIds: Set<string>;
  cellKeys: Set<string>;
};

export const selectedCellKey = (clusterId: string, id: string) =>
  `${clusterId}|${id}`;

export const useSelectedProducts = (
  rows: WideOrderBookRow[]
): SelectedProducts => {
  const { selectedProducts } = usePrefillContext();

  return useMemo(() => {
    const rowIds = new Set<string>();
    const cellKeys = new Set<string>();

    if (selectedProducts.length === 0) {
      return { rowIds, cellKeys };
    }

    const known = new Set<string>();
    for (let i = 0; i < rows.length; i++) {
      known.add(rows[i].id);
    }

    for (let i = 0; i < selectedProducts.length; i++) {
      const product = selectedProducts[i];
      if (!product) {
        continue;
      }

      const id = rowId(product.delivery_start, product.delivery_end);
      if (!known.has(id)) {
        continue;
      }

      rowIds.add(id);
      cellKeys.add(
        selectedCellKey(clusterOfArea(product.delivery_area_id).id, id)
      );
    }

    return { rowIds, cellKeys };
  }, [rows, selectedProducts]);
};
